import React from 'react';

import {V2RequestsFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import * as BenchlingUtil from 'BenchlingUtil';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {getAccessibleOrInaccessibleId} from 'graphql-client/InaccessibleInfo';
import BaseListingRendererWithPlugins from 'nav/expanded/BaseListingRendererWithPlugins';
import {
  ColumnAccessorKey,
  ColumnConfiguration,
  ListingRendererType,
} from 'nav/expanded/ListingRendererConstants';
import {getPartyDisplayString} from 'parties/GetPartyDisplayString';
import {getTasksFilteredByProcessesUrl} from 'processes/dashboard/getModelFilteredByProcessesrUrl';
import * as ProcessManagementContextMenu from 'processes/dashboard/ProcessManagementContextMenu';
import {ProcessTaskStatusCounts} from 'processes/dashboard/ProcessTaskStatusCounts';
import {PROCESS_DASHBOARD_TASK_STATUS_COLUMN_WIDTH} from 'processes/ProcessConstants';
import {processExecutionTypeDisplayName} from 'processes/ProcessConstants';
import RequestV2SubmissionChip from 'processes/requests_v2/chips/RequestV2SubmissionChip';
import * as ExpandedTableTagUtil from 'schemas/tags/ExpandedTableTagUtil';
import {SortKey} from 'search/SearchConstants';
import {getColumnHeaderFromSortKey} from 'search/SortUtil';
import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonStyle} from 'ui-library/buttons/Button';
import {ChipSize} from 'ui-library/chips/Chip';
import ProcessChip from 'ui-library/chips/ProcessChip';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';
import {DatetimeTableCell} from 'ui-library/scrollable/table-cells/DatetimeTableCell';
import {BasicEvent} from 'util/Types';

import {ProcessListingGraphQL} from '../../../../../../graphql-fragments/processes/__generated/ProcessListingGraphQL';

export interface ProcessListingExtras {}

export default class ProcessListingRenderer extends BaseListingRendererWithPlugins<ListingRendererType.PROCESS> {
  listingRendererType = ListingRendererType.PROCESS;

  renderIcon(): JSX.Element {
    return <BenchlingIcon icon="process" className="scrollableTable-cell-icon" />;
  }

  getNonPluginColumnConfigurations(
    items: Array<ProcessListingGraphQL>
  ): Array<ColumnConfiguration<ProcessListingGraphQL>> {
    const shouldDisplayRequestV2SubmissionColumn =
      isFeatureEnabled({feature: V2RequestsFeature.V2_REQUESTS_DISCOVER}) &&
      items.some((item) => item.requestV2Submission != null);

    const columns: Array<ColumnConfiguration<ProcessListingGraphQL>> = [
      {
        column: {
          accessorKey: ColumnAccessorKey.ID,
          header: 'ID',
          isSortable: true,
          sortKey: SortKey.SORT_IDENTIFIER_KEY,
          flexGrow: 1,
          width: 200,
        },
        render: (item) => item.humanId,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.SCHEMA,
          header: 'Schema',
          isSortable: true,
          sortKey: SortKey.SORT_SCHEMA_NAME,
          flexGrow: 1,
        },
        render: (item) => item.processTaskSchema.name,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.NAME,
          header: 'Name',
          isSortable: true,
          sortKey: SortKey.SORT_NAME_KEY,
          flexGrow: 1,
        },
        render: (item) => item.name,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.TASK_STATUS,
          header: 'Task status',
          width: PROCESS_DASHBOARD_TASK_STATUS_COLUMN_WIDTH,
        },
        render: (item) => (
          <Tooltip title={`View tasks of ${item.name}`} placement={TooltipPlacement.LEFT}>
            <Button
              buttonStyle={ButtonStyle.UNBUTTON}
              className="u-width-full"
              onClick={(e) => {
                e.stopPropagation();
                BenchlingUtil.navigate(
                  e,
                  getTasksFilteredByProcessesUrl([item.id], item.processTaskSchema.id)
                );
              }}
            >
              <ProcessTaskStatusCounts process={item} />
            </Button>
          </Tooltip>
        ),
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.EXECUTION_TYPE,
          header: 'Execution type',
          isSortable: true,
          sortKey: SortKey.SORT_EXECUTION_TYPE,
          flexGrow: 1,
        },
        render: (item) =>
          processExecutionTypeDisplayName(
            item.processTaskSchema.clientOrigin,
            item.processTaskSchema.executionType
          ),
      },
      ...(shouldDisplayRequestV2SubmissionColumn
        ? [
            {
              column: {
                accessorKey: ColumnAccessorKey.REQUEST_V2_SUBMISSION,
                header: 'Request',
                flexGrow: 1,
                isSortable: false,
              },
              render: (item: ProcessListingGraphQL) => {
                if (item.requestV2Submission != null) {
                  return (
                    <RequestV2SubmissionChip
                      id={getAccessibleOrInaccessibleId(item.requestV2Submission)}
                      size={ChipSize.MINI}
                    />
                  );
                }
                return null;
              },
            },
          ]
        : []),
      {
        column: {
          accessorKey: ColumnAccessorKey.ROOT_PROCESS,
          header: 'Flowchart task group',
          isSortable: false,
          flexGrow: 1,
        },
        render: (item) =>
          item.rootProcess != null ? (
            <ProcessChip id={getAccessibleOrInaccessibleId(item.rootProcess)} size={ChipSize.MINI} />
          ) : null,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.CREATOR,
          header: 'Creator',
          isSortable: true,
          sortKey: SortKey.SORT_CREATOR_NAME,
          flexGrow: 1,
        },
        render: (item) => getPartyDisplayString(item.creator),
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.CREATED_AT,
          header: getColumnHeaderFromSortKey(SortKey.SORT_CREATED_AT_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_CREATED_AT_KEY,
        },
        render: (item) => <DatetimeTableCell datetime={item.createdAt} showTimeInCell={false} />,
      },
      {
        column: {
          accessorKey: ColumnAccessorKey.MODIFIED_AT,
          header: getColumnHeaderFromSortKey(SortKey.SORT_MODIFIED_AT_KEY),
          flexGrow: 1,
          isSortable: true,
          sortKey: SortKey.SORT_MODIFIED_AT_KEY,
        },
        render: (item) => <DatetimeTableCell datetime={item.modifiedAt} showTimeInCell={false} />,
      },
    ];

    return columns;
  }

  getNonPluginRowContentHeight(item: ProcessListingGraphQL): number {
    return ExpandedTableTagUtil.getRowHeight([]);
  }

  openContextMenu(item: ProcessListingGraphQL, e: BasicEvent, extras: unknown): void {
    ProcessManagementContextMenu.open(e.nativeEvent, ModelTypeConstants.PROCESS, item);
  }
}
